import React, { useMemo, useState, useEffect } from "react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt, cn } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

// Heparin sediaan default: 25,000 units in 50 cc → 500 units / cc
const DEFAULT_TOTAL_UNITS = 25000;
const DEFAULT_TOTAL_VOLUME = 50;

const INDICATIONS = {
  acs: {
    id: "acs",
    bolusPerKg: 60,
    bolusMaxUnits: 4000,
    maintPerKgHr: 12,
    maintMaxUnitsHr: 1000,
  },
  vte: {
    id: "vte",
    bolusPerKg: 80,
    bolusMaxUnits: 10000,
    maintPerKgHr: 18,
    maintMaxUnitsHr: 2000,
  },
};

export default function Heparin() {
  const t = useT();
  const [indication, setIndication] = useState("acs");

  const [weight, setWeight] = useState("");
  const [totalUnits, setTotalUnits] = useState(String(DEFAULT_TOTAL_UNITS));
  const [totalVolume, setTotalVolume] = useState(String(DEFAULT_TOTAL_VOLUME));
  const [bolusPerKg, setBolusPerKg] = useState(String(INDICATIONS.acs.bolusPerKg));
  const [maintPerKgHr, setMaintPerKgHr] = useState(String(INDICATIONS.acs.maintPerKgHr));

  // Reset bolus / maintenance defaults whenever indication switches
  useEffect(() => {
    const ind = INDICATIONS[indication];
    setBolusPerKg(String(ind.bolusPerKg));
    setMaintPerKgHr(String(ind.maintPerKgHr));
  }, [indication]);

  const ind = INDICATIONS[indication];
  const w = parseFloat(weight);
  const u = parseFloat(totalUnits);
  const v = parseFloat(totalVolume);
  const bk = parseFloat(bolusPerKg);
  const mk = parseFloat(maintPerKgHr);

  const calc = useMemo(() => {
    if (!w || !u || !v || w <= 0 || u <= 0 || v <= 0) return null;
    const unitsPerCc = u / v;

    // Bolus with hard cap
    const rawBolusUnits = bk * w;
    const bolusUnits = Math.min(rawBolusUnits, ind.bolusMaxUnits);
    const bolusCapped = rawBolusUnits > ind.bolusMaxUnits;
    const bolusCc = bolusUnits / unitsPerCc;

    // Maintenance with hard cap
    const rawMaintUnitsHr = mk * w;
    const maintUnitsHr = Math.min(rawMaintUnitsHr, ind.maintMaxUnitsHr);
    const maintCapped = rawMaintUnitsHr > ind.maintMaxUnitsHr;
    const maintCcHr = maintUnitsHr / unitsPerCc;
    const maintMgHr = maintUnitsHr / 100; // 100 U = 1 mg

    return {
      unitsPerCc,
      rawBolusUnits,
      bolusUnits,
      bolusCc,
      bolusCapped,
      rawMaintUnitsHr,
      maintUnitsHr,
      maintCcHr,
      maintMgHr,
      maintCapped,
    };
  }, [w, u, v, bk, mk, ind]);

  return (
    <div data-testid="page-heparin">
      <PageHeader
        title={t("hep.title")}
        subtitle={t("hep.subtitle")}
        formula={t("hep.formula")}
      />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          {/* Indication toggle */}
          <Field label={t("hep.indication")} testid="indication">
            <div
              className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full"
              role="group"
              data-testid="indication-group"
            >
              {[
                { v: "acs", labelKey: "hep.indication.acs" },
                { v: "vte", labelKey: "hep.indication.vte" },
              ].map((opt) => {
                const active = indication === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`indication-${opt.v}`}
                    onClick={() => setIndication(opt.v)}
                    className={cn(
                      "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px]",
                      active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                    )}
                    aria-pressed={active}
                  >
                    {t(opt.labelKey)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t("hep.weight")} unit="kg" testid="weight">
            <NumberInput value={weight} onChange={setWeight} placeholder="e.g. 70" min={0} testid="weight-input" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("hep.total_units")} unit="U" testid="total-units">
              <NumberInput value={totalUnits} onChange={setTotalUnits} placeholder="25000" min={0} testid="units-input" />
            </Field>
            <Field label={t("hep.total_volume")} unit="cc" testid="total-volume">
              <NumberInput value={totalVolume} onChange={setTotalVolume} placeholder="50" min={0} testid="volume-input" />
            </Field>
          </div>

          <Field
            label={t("hep.bolus_target")}
            unit="U/kg"
            testid="bolus"
            hint={t("hep.bolus_hint", { def: ind.bolusPerKg, max: ind.bolusMaxUnits.toLocaleString() })}
          >
            <NumberInput value={bolusPerKg} onChange={setBolusPerKg} placeholder={String(ind.bolusPerKg)} min={0} testid="bolus-input" />
          </Field>

          <Field
            label={t("hep.maint_target")}
            unit="U/kg/h"
            testid="maint"
            hint={t("hep.maint_hint", { def: ind.maintPerKgHr, max: ind.maintMaxUnitsHr.toLocaleString() })}
          >
            <NumberInput value={maintPerKgHr} onChange={setMaintPerKgHr} placeholder={String(ind.maintPerKgHr)} min={0} testid="maint-input" />
          </Field>

          <Note kind="warning">{t("hep.note")}</Note>
        </div>

        <div className="space-y-4">
          <Result
            label={t("hep.result.bolus_units")}
            value={calc ? fmt(calc.bolusUnits, 0) : "—"}
            unit="Units"
            big
            testid="result-bolus-units"
            accent={calc?.bolusCapped ? "danger" : "default"}
          />
          {calc?.bolusCapped && (
            <div
              className="text-xs text-amber-900 bg-amber-50 border border-amber-700/40 rounded-sm px-3 py-2"
              data-testid="bolus-cap-warning"
            >
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase font-semibold bg-amber-700/10 px-1.5 py-0.5 rounded-sm mr-2">
                {t("hep.cap_badge")}
              </span>
              {t("hep.cap_warning", {
                raw: fmt(calc.rawBolusUnits, 0),
                max: ind.bolusMaxUnits.toLocaleString(),
              })}
            </div>
          )}
          <Result
            label={t("hep.result.bolus_cc")}
            value={calc ? fmt(calc.bolusCc, 2) : "—"}
            unit="cc"
            testid="result-bolus-cc"
          />
          <Result
            label={t("hep.result.maint_cc")}
            value={calc ? fmt(calc.maintCcHr, 2) : "—"}
            unit="cc / hr"
            big
            testid="result-maint-cc"
            accent={calc?.maintCapped ? "danger" : "default"}
          />
          {calc?.maintCapped && (
            <div
              className="text-xs text-amber-900 bg-amber-50 border border-amber-700/40 rounded-sm px-3 py-2"
              data-testid="maint-cap-warning"
            >
              <span className="font-mono text-[10px] tracking-[0.12em] uppercase font-semibold bg-amber-700/10 px-1.5 py-0.5 rounded-sm mr-2">
                {t("hep.cap_badge")}
              </span>
              {t("hep.cap_warning", {
                raw: fmt(calc.rawMaintUnitsHr, 0),
                max: ind.maintMaxUnitsHr.toLocaleString(),
              })}
            </div>
          )}
          <Result
            label={t("hep.result.maint_units")}
            value={
              calc
                ? `${fmt(calc.maintUnitsHr, 0)} U / hr  ·  ${fmt(calc.maintMgHr, 2)} mg / hr`
                : "—"
            }
            testid="result-maint-units"
          />
          <FormulaBox testid="breakdown-box">
            {calc
              ? `Indication = ${indication.toUpperCase()}  ·  Caps: bolus ${ind.bolusMaxUnits.toLocaleString()} U, maint ${ind.maintMaxUnitsHr.toLocaleString()} U/hr
Concentration = ${u} U ÷ ${v} cc = ${fmt(calc.unitsPerCc, 0)} U/cc
Bolus  = ${bk} U/kg × ${fmt(w, 1)} kg = ${fmt(calc.rawBolusUnits, 0)} U${
                  calc.bolusCapped ? `  →  capped at ${ind.bolusMaxUnits.toLocaleString()} U` : ""
                }  →  ${fmt(calc.bolusCc, 2)} cc IV push
Maint. = ${mk} U/kg/h × ${fmt(w, 1)} kg = ${fmt(calc.rawMaintUnitsHr, 0)} U/hr${
                  calc.maintCapped ? `  →  capped at ${ind.maintMaxUnitsHr.toLocaleString()} U/hr` : ""
                }  →  ${fmt(calc.maintCcHr, 2)} cc/hr
Mass equivalent (100 U = 1 mg) = ${fmt(calc.maintMgHr, 2)} mg/hr`
              : t("hep.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
